/* 
  fraud === Fraud Modal
  save === pending modal
  cancel === cancel modal
  approve === approve modal
  validation === validation modal
  add-fund === add-fund
  refund ==== refund modal
* */

import OrderApprove from "Assets/images/order-approve.png";
import OrderCancel from "Assets/images/order-cancel.png";
import OrderSave from "Assets/images/order-save.png";
import { AppContext } from "Context/AppContext";
import { useFormikContext } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { useOrderAttributeProvider } from "../context/order";
import { useProcessingOrder } from "../hooks/useProcessingOrder";
import CancelOrder from "./CancelOrder";
import Error from "./Error";
import ModalWithNote from "./ModalWithNote";
import ModalWithReasons from "./ModalWithReasons";
import ModalWithRefunds from "./ModalWithRefunds";
import FundModal from "./OrderFund";
import DiscountModal from "./OrderDiscount/OrderDiscount";
import OverrideEditOrderModal from "./OverrideEditOrderModal";
import { ORDER_SOURCE, PAYMENT_METHODS } from "Helpers/Constants";
import { isRefundSupported } from "Helpers/refundSupport";
import RefundPriceModel from "./refundPriceModel";
import Confirmation from "Components/Modal/Confirmation/Confirmation";
import { addInternalNotes } from "api/order";
import { useUser } from "Context/UserContext/useUser";

const EditOrderSubmit = () => {
  const {
    modal,
    setModal,
    orderDetail,
    setOrderDetails,
    summary,
    setSummary,
    internalNoteDraft,
    setInternalNoteDraft,
    estimating,
  } = useOrderAttributeProvider();
  const { userState } = useUser();
  const {
    showErrorModal,
    setShowErrorModal,
    makeOrderFraud,
    cancelOrderCall,
    saveOrder,
    approveOrder,
    validate,
    orderAssignCheck,
    overRideEditOrder,
    cancleOverRideEditOrder,
  } = useProcessingOrder();
  const [fundType, setFundType] = useState("");
  const { setIsLoading, showToastMessage, lastOrderEditPath } = useContext(
    AppContext
  );
  const [allowEdit, setAllowEdit] = useState(true);
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [allowEditMessage, setAllowEditMessage] = useState("");
  const [originalStore, setOriginalStore] = useState("");
  const [showBurqOrderModal, setShowBurqOrderModal] = useState(false);
  const [showTransferOrderWarning, setShowTransferOrderWarning] = useState({
    show: false,
    modelOption: "",
    type: ""
  });

  useEffect(() => {
    if(orderDetail){
      setOriginalStore(orderDetail?.original_store?.title || "")
    }
  }, [orderDetail])
  const proceedClickPopup = async () => {
    overRideEditOrder();
    setModal("");
    window.location.reload();
  };

  const formik = useFormikContext();
  const changeValue = (value) => {
    let val = value || "";
    setSummary({
      ...summary,
      add_fund: fundType === "add-fund" ? val : summary.add_fund,
      refund: fundType === "refund" ? val : summary.refund,
    });
  };

  const handleCancelOrder = () => {
    const refundReason = formik.values?.refund_reason;
    if (refundReason?.reason_type_id && refundReason?.reason_subtype_id) {
      setModal("cancel-refund");
    }
  }
  const handleCancelRefundOrder = (optionVal, refundVal) => {
    const refundReason = formik.values?.refund_reason;
    const cancelOrderData = {
      order_id: orderDetail.id,
      refund: refundVal ?? 0,
      fullRefundFlag: optionVal === 2 ? true : false,
      reason_team: refundReason?.reason_team,
      reason_responsible_person: refundReason?.reason_responsible_person,
      reason_message: refundReason?.reason_message,
      reason_type_id: refundReason?.reason_type_id,
      reason_subtype_id: refundReason?.reason_subtype_id,
      reason_message_option: refundReason?.reason_subtype_id,
      notes: refundReason?.reason_subtype_name,
      refund_flag: optionVal,
    }
    cancelOrderCall(cancelOrderData);
    setModal("");
  }

  const handleRefundOrder = () => {
    const refundReason = formik.values?.refund_reason;
    if (refundReason?.reason_type_id && refundReason?.reason_subtype_id) {
      setModal("refund-order");
    }
  }

  const handleRefundRefundOrder = (optionVal, refundVal) => {
    summary.refund = fundType === "refund" ? refundVal : summary.refund;
    orderDetail.refund_flag = optionVal;
    const refundReason = formik.values?.refund_reason;
    setModal("");
    approveOrder(refundReason?.reason_subtype_name);
  }

  if ((orderDetail?.order_status === "cancel" && formik.values.is_burq_order !== 1) || (orderDetail?.order_status === "cancel" && formik.values.is_burq_order === 1 && formik.values.is_cancel_fail_by_burq !== 1))
    return (
      <>
        <ModalWithNote
          showModal={modal === "fraud"}
          handleCancelClick={() => setModal("")}
          handleSubmitClick={makeOrderFraud}
          pageTitle="Fraud Order"
        />
      </>
    );

  const order_payment_transactions_data = formik.values?.order_payment_transactions?.filter((val) => val.order_id === 0) || []
  const payment_method = order_payment_transactions_data?.length > 0 && order_payment_transactions_data[0]?.payment_method
  // The API decides per transaction whether a refund is possible; Braintree-era
  // rows come back false, so they stay locked.
  const refund_supported = isRefundSupported(order_payment_transactions_data[0])
  const isDisebled = formik.values.source === ORDER_SOURCE.WALKIN.toLowerCase() && payment_method === PAYMENT_METHODS.PAYABLI ? true : false
  const addFundOrRefund = summary.add_fund || summary.refund
  const addFundOrRefundValidation = addFundOrRefund && !refund_supported

  const handleTransferOrderWarning = (option = "", type = "") => {
    setShowTransferOrderWarning({show: false, modelOption: option, type: type});
    if(type === "addFundRefund"){
      setFundType(option);
      if (option === "add-fund") {
        setModal("fund-approve");
      }
    }
  }
  
  /**
   * Handles the submission of an edited order with comprehensive validation and workflow management.
   * 
   * This function orchestrates the order approval process by:
   * 1. Validating discount constraints
   * 2. Checking order assignment permissions
   * 3. Performing order validation
   * 4. Determining fund operations (add-fund/refund)
   * 5. Handling special cases (transfer orders, validations, processing status)
   * 6. Routing to appropriate modals or directly approving the order
   * 
   * @returns {Promise<void>}
   */
  // Flush an unsent internal-note draft so clicking Save doesn't discard it
  // (the note box has its own submit). Called on the Save click before the burq
  // branch, so it covers burq orders too — even if the burq modal is cancelled.
  const flushInternalNoteDraft = async () => {
    if (internalNoteDraft && internalNoteDraft.trim()) {
      await addInternalNotes({
        order_id: orderDetail.id,
        created_by: userState.userProfile?.id,
        notes: internalNoteDraft.trim(),
      });
      setInternalNoteDraft("");
    }
  };

  const handleEditOrderSubmit = async () => {
    // Step 1: Validate discount constraint - ensure flower total is not less than discount price
    if (summary.flower_total_amount >= summary.discount_price) {
      // Step 2: Check if order can be edited (assignment and permission validation)
      let checkOrderAssign = await orderAssignCheck(true);
      if (!checkOrderAssign.data.editFlag) {
        // Order cannot be edited - show override modal with appropriate message
        setAllowEdit(checkOrderAssign.data.allowEdit ? false : true);
        setAllowEditMessage(checkOrderAssign.message);
        setModal("override-order");
        return;
      }
      
      // Step 3: Validate order data (form fields, business rules, etc.)
      let error = await validate();
      if (error) {
        // Validation failed - error handling is done in validate function
        return;
      }
      
      // Step 4: Determine fund operation type based on add_fund and refund amounts
      let fundType = "";
      if (+parseFloat(summary.add_fund).toFixed(0) > 0) {
        // Add fund operation - only allowed for specific payment types
        if (formik.values.payment_type !== "credit_card" && formik.values.payment_type !== "house_account" && formik.values.payment_type !== "dove" && formik.values.payment_type !== "cash") {
          showToastMessage(
            undefined,
            `Add fund only available for orders done through credit/debit cards.`,
            "error",
            false
          );
          return;
        }
        fundType = "add-fund";
      } else if (+parseFloat(summary.refund).toFixed(0) > 0) {
        // Refund operation detected
        fundType = "refund";
      } else if (
        parseFloat(summary.add_fund) === 0 &&
        parseFloat(summary.refund) === 0
      ) {
        // No fund operations required
        fundType = "";
      }
      
      // Step 5: Handle fund operations workflow
      if (fundType) {
        // Special handling for transfer orders with add-fund operation
        // Transfer orders require confirmation since funds go to original store
        if(formik.values.is_transfer_order && (fundType === "add-fund")){
          setShowTransferOrderWarning({show: true, modelOption: fundType, type: "addFundRefund"});
          return;
        }else{
          // Set fund type and show appropriate modal
          setFundType(fundType);
          if (fundType === "refund") {
            // Refund requires reason selection first
            setModal("refund-reason")
          } else {
            // Add fund goes directly to approval modal
            setModal("fund-approve");
          }
        }
      } 
      // Step 6: Handle orders without fund operations
      else if ((orderDetail.order_validations.length !== 0 && !addFundOrRefundValidation)  || (orderDetail?.order_status.toLowerCase() === "processing" && !orderDetail.is_local_order)) {
        // Order has validations or is in processing status - requires override confirmation
        setModal("override");
      } else if(addFundOrRefundValidation){
        // Add fund/refund validation failed (e.g., Braintree payment method restriction)
        setModal("")
        showToastMessage(undefined, 'This feature is not available for this order.', "error", false);
        return;
      } else {
        // All checks passed - approve order directly
        approveOrder();
      }
    } else {
      // Discount validation failed - show discount modal to fix the issue
      setShowDiscountModal(true)
    }
  }

  return (
    <>
      <Error
        open={showErrorModal}
        handleClose={() => setShowErrorModal(false)}
      />
      <div className="d-flex justify-content-center align-items-center mt-5 mb-5">
        <div className="mr-4">
          <img
            src={OrderApprove}
            style={{ width: "60px", height: "60px", opacity: estimating ? 0.4 : 1, cursor: estimating ? "not-allowed" : "pointer" }}
            title={estimating ? "Tax is being calculated" : "Approve Order"}
            alt="Approve Order"
            onClick={estimating ? undefined : async () => {
              // Save the pending internal-note draft first, so it isn't lost on
              // the burq branch (or if the burq modal is cancelled).
              await flushInternalNoteDraft();
              //Check if order is burq order and if it is, then show the burq order modal
              if(formik.values.is_burq_order && formik.values.is_burq_order === 1){
                setShowBurqOrderModal(true);
                return;
              }
              await handleEditOrderSubmit();
            }}
          />
        </div>

        {orderDetail?.order_status !== "cancel"
          && refund_supported && (
          <div>
            <img
              src={OrderCancel}
              onClick={async () => {
                let checkOrderAssign = await orderAssignCheck(true);
                if (!checkOrderAssign.data.editFlag) {
                  setAllowEdit(checkOrderAssign.data.allowEdit ? false : true);
                  setAllowEditMessage(checkOrderAssign.message);
                  setModal("override-order");
                  return;
                }
                setModal("cancel-confirm");
              }}
              alt="Cancel order"
              style={{ width: "40px", height: "40px" }}
            />
          </div>
        )}
      </div>

      <DiscountModal
        showModal={showDiscountModal}
        pageTitle="Add Discount"
        submitStatus={false}
        handleCancelClick={(e) => {
          setShowDiscountModal(false);
        }}
      />

      <ModalWithNote
        showModal={modal === "fraud"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={makeOrderFraud}
        pageTitle="Fraud Order"
      />
      <ModalWithReasons
        showModal={modal === "cancel-reason"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={handleCancelOrder}
        pageTitle="Cancel Order"
        placeholder="cancelling"
        totalRefundAmount={formik.values.total_refund_pending}
      />
      {
        modal === 'cancel-refund' &&  <ModalWithRefunds
        showModal={modal === "cancel-refund"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={handleCancelRefundOrder}
        pageTitle="Cancel Order"
        placeholder="cancelling"
        totalRefundAmount={formik.values.total_refund_pending}
        summary={summary}
        setModal={setModal}
      />
      }
     
      <ModalWithNote
        showModal={modal === "approve-validation"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={approveOrder}
        pageTitle="Override Order"
      />
      <ModalWithNote
        showModal={modal === "save"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={saveOrder}
        pageTitle="Pending Order"
        placeholder="Why is this order pending?"
      />
      <CancelOrder
        showModal={modal === "cancel-confirm"}
        pageTitle="Cancel Order"
        handleCancelClick={() => setModal("")}
        handleSubmitClick={() => setModal("cancel-reason")}
      />
      <FundModal  
        open={modal === "fund-save"}
        pageTitle={fundType === "add-fund" ? "Add Fund" : "Refund"}
        value={fundType === "add-fund" ? summary.add_fund : summary.refund}
        onClose={() => setModal("")}
        onChange={changeValue}
        onSave={() => {
          setModal("save");
        }}
      />

      {showTransferOrderWarning.show && (
        <Confirmation
          btn1_title={"Continue"}
          btn2_title={"Cancel"}
          handleCancelClick={() => setShowTransferOrderWarning({show: false, modelOption: "", type: "addFundRefund"})}
          showModal={showTransferOrderWarning.show}
          handleSubmitClick={() => handleTransferOrderWarning(showTransferOrderWarning.modelOption, showTransferOrderWarning.type)}
          message={`This order was originally placed at the ${originalStore} store. If you add funds, they’ll automatically go to that location. No action is needed — the funds will be reconciled automatically at the end of the month.`}
          title={"Confirmation"}
          key={"confirmation-transfer-order"}
      />
      )}

       {showBurqOrderModal && (
        <Confirmation
          btn1_title={"Continue"}
          btn2_title={"Cancel"}
          handleCancelClick={() => setShowBurqOrderModal(false)}
          showModal={showBurqOrderModal}
          handleSubmitClick={async () => {
            setShowBurqOrderModal(false);
            await handleEditOrderSubmit();
          }}
          message={`If this order requires Burq to be updated, you must do so manually; Burq does not support automatic updates at this time`}
          title={"Confirmation"}
          key={"confirmation-burq-order"}
      />
      )}
      <ModalWithReasons
        showModal={modal === "refund-reason"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={handleRefundOrder}
        pageTitle="Refund"
        placeholder="refunding"
        totalRefundAmount={formik.values.total_refund_pending}
      />
      {
        modal === "refund-order" && <RefundPriceModel
        showModal={modal === "refund-order"}
        handleCancelClick={() => setModal("")}
        handleSubmitClick={(refundOptions, refundAmount)=>{
          if(addFundOrRefundValidation){
            setModal("")
            showToastMessage(undefined, 'This feature is not available for this order.', "error", false);
            return;
          }else{
            handleRefundRefundOrder(refundOptions, refundAmount)
          }
        }}
        pageTitle="Refund"
        placeholder="refunding"
        totalRefundAmount={formik.values.total_refund_pending}
        summary={summary}
        setModal={setModal}
      />
      }
      
      <FundModal
        open={modal === "fund-approve"}
        onClose={() => setModal("")}
        pageTitle={fundType === "add-fund" ? "Add Fund" : "Refund"}
        value={fundType === "add-fund" ? summary.add_fund : summary.refund}
        disebled={true}
        onChange={changeValue}
        onSave={() => {
          if (orderDetail.order_validations.length !== 0 && !addFundOrRefundValidation) {
            setModal("override");
          } else {
            if (summary.add_fund > 0 && isDisebled) {
              setModal("")
              showToastMessage(undefined, 'This feature is not available for walk in orders.', "error", false);
            }else if(addFundOrRefundValidation){
              setModal("")
              showToastMessage(undefined, 'This feature is not available for this order.', "error", false);
              return;
            } else {
              approveOrder();
            }
          }
        }}
      />

      <OverrideEditOrderModal
        showModal={modal === "override-order"}
        handleCancelClick={cancleOverRideEditOrder}
        handleSubmitClick={proceedClickPopup}
        pageTitle="Override Edit Order"
        hideProceedButton={allowEdit}
        message={allowEditMessage}
      />
    </>
  );
};

export default EditOrderSubmit;
